import React from 'react';
import './dropdown-menu.css';
import Tooltip from '../tooltip/Tooltip';
import { Link, useNavigate } from 'react-router-dom';
import { IPopupProps } from '../popup/Popup';

export interface IDropdownMenuCardOption<Value = string> {
	onClick?: (
		value: Value,
		event: React.MouseEvent<HTMLButtonElement | HTMLAnchorElement>,
	) => void;
	id?: string;
	navigateTo?: string;
	label?: string;
	subLabel?: string;
	child?: string | React.ReactNode;
	iconEnd?: string;
	iconEndSrc?: string;
	iconEndSrcHover?: string;
	iconStart?: string;
	iconStartSrc?: string;
	iconStartSrcHover?: string;
	iconStartClasses?: string;
	iconEndClasses?: string;
	iconStartStyles?: React.CSSProperties;
	iconEndStyles?: React.CSSProperties;
	color?: 'primary' | 'secondary' | 'error' | 'warning' | 'success' | 'grey';
	tooltip?: string;
	disabled?: boolean;
	value?: Value;
	classes?: string[];
	style?: React.CSSProperties;
	popupOptions?: Partial<IPopupProps>;
}

export interface IDropdownMenuCardProps<Value> {
	options: IDropdownMenuCardOption<Value>[];
	value?: Value;
	header?: string | React.ReactNode;
}

const DropdownMenuCard = <Value extends string = string>({
	options = [],
	value,
	header,
}: IDropdownMenuCardProps<Value>) => {
	return (
		<div className='dropdown-menu-card'>
			{header}
			{options.map(
				({
					label,
					subLabel,
					iconEnd,
					iconEndSrc,
					iconEndSrcHover,
					iconStart,
					iconStartSrc,
					iconStartSrcHover,
					iconStartClasses,
					iconStartStyles,
					iconEndClasses,
					iconEndStyles,
					color = 'primary',
					tooltip,
					disabled = false,
					navigateTo,
					value: optionValue,
					child,
					style,
					onClick,
					id,
					popupOptions,
				}) => {
					// Random id
					const resolvedId = id || Math.random().toString(36).substring(2, 9);
					const selected = value === optionValue && !!value;

					return (
						<span key={resolvedId}>
							{child ? (
								child
							) : navigateTo ? (
								<Link
									style={style}
									to={navigateTo}
									// onClick={(e) => {
									// 	e.preventDefault();
									// 	if (!disabled) {
									// 		navigate(navigateTo);
									// 	}
									// }}
									className={[
										'dropdown-menu-card-option',
										`dropdown-menu-card-option__color-${color}`,
										disabled ? 'dropdown-menu-card-option__disabled' : '',
										selected && !disabled
											? 'dropdown-menu-card-option__selected'
											: '',
									].join(' ')}
								>
									{iconStart && (
										<i
											className={`${iconStart} ${iconStartClasses || ''}`}
											style={iconStartStyles}
										/>
									)}
									{iconStartSrc && (
										<img
											className={
												(!!iconStartSrcHover
													? 'dropdown-menu-option-icon-start-img__hoverable '
													: '') +
												'dropdown-menu-option-icon-start-img-default ' +
												(iconStartClasses || '')
											}
											style={iconStartStyles}
											src={iconStartSrc}
										/>
									)}
									{iconStartSrcHover && (
										<img
											className={
												'dropdown-menu-option-icon-start-img-hover ' +
												(iconStartClasses || '')
											}
											style={iconStartStyles}
											src={iconStartSrcHover}
										/>
									)}
									<div
										style={{
											display: 'flex',
											flexDirection: 'column',
											lineHeight: 'normal',
											gap: 2,
										}}
									>
										{label}
										{subLabel && (
											<p className='dropdown-menu-card-option__sub-label'>
												{subLabel}
											</p>
										)}
									</div>
									{!iconEnd &&
										!iconStart &&
										!iconEndSrc &&
										!iconEndSrcHover &&
										!iconStartSrc &&
										!iconStartSrcHover && (
											<i
												className={`fa-solid fa-arrow-right ${
													iconEndClasses || ''
												}`}
												style={iconEndStyles}
											/>
										)}
									{!!iconEnd &&
										!iconStart &&
										!iconEndSrc &&
										!iconEndSrcHover &&
										!iconStartSrc &&
										!iconStartSrcHover && (
											<i
												className={`${iconEnd} ${iconEndClasses || ''}`}
												style={iconEndStyles}
											/>
										)}
									{!!iconEndSrc && (
										<img
											className={
												'dropdown-menu-option-icon-end-img-default ' +
													iconEndClasses || ''
											}
											src={iconEndSrc}
											style={iconEndStyles}
										/>
									)}
									{!!iconEndSrcHover && (
										<img
											className={
												'dropdown-menu-option-icon-end-img-hover ' +
													iconEndClasses || ''
											}
											src={iconEndSrcHover}
											style={iconEndStyles}
										/>
									)}
								</Link>
							) : (
								<button
									style={style}
									onClick={(e) => {
										onClick?.(optionValue, e);
									}}
									id={`dropdown-menu-card-option--${resolvedId}`}
									className={[
										'dropdown-menu-card-option',
										`dropdown-menu-card-option__color-${color}`,
										disabled ? 'dropdown-menu-card-option__disabled' : '',
										selected && !disabled
											? 'dropdown-menu-card-option__selected'
											: '',
									].join(' ')}
									disabled={disabled}
								>
									<span
										style={{
											display: 'flex',
											gap: '14px',
											alignItems: 'center',
										}}
										className='dropdown-menu-card-option__content'
									>
										{iconStart && (
											<i
												className={`${iconStart} ${iconStartClasses || ''}`}
												style={iconStartStyles}
											/>
										)}
										{iconStartSrc && (
											<img
												className={
													(!!iconStartSrcHover
														? 'dropdown-menu-option-icon-start-img__hoverable '
														: '') +
													'dropdown-menu-option-icon-start-img-default ' +
													(iconStartClasses || '')
												}
												style={iconStartStyles}
												src={iconStartSrc}
											/>
										)}
										{iconStartSrcHover && (
											<img
												className={
													'dropdown-menu-option-icon-start-img-hover ' +
													(iconStartClasses || '')
												}
												style={iconStartStyles}
												src={iconStartSrcHover}
											/>
										)}
										<div
											style={{
												display: 'flex',
												flexDirection: 'column',
												lineHeight: 'normal',
												gap: 2,
											}}
										>
											{label}
											{subLabel && (
												<p className='dropdown-menu-card-option__sub-label'>
													{subLabel}
												</p>
											)}
										</div>
									</span>
									{!iconEnd &&
										!iconStart &&
										!iconEndSrc &&
										!iconEndSrcHover &&
										!iconStartSrc &&
										!iconStartSrcHover && (
											<i
												className={`fa-solid fa-arrow-right ${
													iconEndClasses || ''
												}`}
												style={iconEndStyles}
											/>
										)}
									{!!iconEnd &&
										!iconStart &&
										!iconEndSrc &&
										!iconEndSrcHover &&
										!iconStartSrc &&
										!iconStartSrcHover && (
											<i
												className={`${iconEnd} ${iconEndClasses || ''}`}
												style={iconEndStyles}
											/>
										)}
									{!!iconEndSrc && (
										<img
											className={
												'dropdown-menu-option-icon-end-img-default ' +
													iconEndClasses || ''
											}
											src={iconEndSrc}
											style={iconEndStyles}
										/>
									)}
									{!!iconEndSrcHover && (
										<img
											className={
												'dropdown-menu-option-icon-end-img-hover ' +
													iconEndClasses || ''
											}
											src={iconEndSrcHover}
											style={iconEndStyles}
										/>
									)}
								</button>
							)}
							{tooltip && (
								<Tooltip
									anchorQuerySelector={`#dropdown-menu-card-option--${resolvedId}`}
									placement='right'
									{...(popupOptions || {})}
								>
									{tooltip}
								</Tooltip>
							)}
						</span>
					);
				},
			)}
		</div>
	);
};

export default DropdownMenuCard;
